import { useTranslation } from "react-i18next";
import React, { FC, useEffect, useState } from "react";
import "./NotificationPanel.scss";
import Toggle from "../Toggle/Toggle";
import { svgs } from "../../common/assats";
import { NotificationType } from "../../common/types/notificationType";
import { useUser } from "../../common/contexts/UserContext";
import {
  filterNotifications,
  getUserNotifications,
  markNotificationAsRead,
} from "../../common/api/notificationService";
import { Alert, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";

interface NotificationPanelProps {
  onClose: () => void;
}

const NotificationPanel: FC<NotificationPanelProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState<NotificationType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Toggles for filter types
  const [newPatientsEnabled, setNewPatientsEnabled] = useState(true);
  const [deviceErrorsEnabled, setDeviceErrorsEnabled] = useState(true);
  const [softwareUpdatesEnabled, setSoftwareUpdatesEnabled] = useState(false);

  const [showPreferences, setShowPreferences] = useState(false);
  const { user } = useUser();
  const navigate = useNavigate();

  // Only Admin can see the preferences
  const canViewPreferences = user?.role === "Admin";

  const fetchNotifications = async () => {
    setLoading(true);
    setError(null);
    try {
      let notificationsData: NotificationType[] = [];
      if (canViewPreferences) {
        notificationsData = await filterNotifications(
          newPatientsEnabled,
          deviceErrorsEnabled,
          softwareUpdatesEnabled
        );
      } else if (user?.id) {
        notificationsData = await getUserNotifications(user.id);
      } else {
        throw new Error("User ID is not available.");
      }
      setNotifications(notificationsData || []);
    } catch (err) {
      console.error(err);
      setError(
        err instanceof Error ? err.message : "An unexpected error occurred."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [newPatientsEnabled, deviceErrorsEnabled, softwareUpdatesEnabled]);

  const handleDismiss = async (id: string) => {
    try {
      await markNotificationAsRead(id);
      setNotifications((prevNotifications) =>
        prevNotifications.filter((notif) => notif.id !== id)
      );
    } catch (err) {
      console.error(`Failed to mark notification ${id} as read`, err);
    }
  };

  return (
    <div className="notification-panel-wrapper">
      <div className="notification-panel">
        <div className="notification-panel-header">
          <h4 className="notification-panel-header-title">
            {t("notifications.title")}
          </h4>
          {canViewPreferences && (
            <span
              className="preferences-toggle-icon"
              onClick={() => setShowPreferences(!showPreferences)}
              style={{ cursor: "pointer" }}
            >
              {showPreferences ? (
                <svgs.icons.PreferencesOpenIcon />
              ) : (
                <svgs.icons.PreferencesClosedIcon />
              )}
            </span>
          )}
        </div>

        {/* Preferences section for Admin */}
        {showPreferences && canViewPreferences && (
          <div className="notification-preferences">
            {[
              {
                label: t("notifications.newPatients"),
                isActive: newPatientsEnabled,
                toggleHandler: setNewPatientsEnabled,
              },
              {
                label: t("notifications.deviceErrors"),
                isActive: deviceErrorsEnabled,
                toggleHandler: setDeviceErrorsEnabled,
              },
              {
                label: t("notifications.softwareUpdates"),
                isActive: softwareUpdatesEnabled,
                toggleHandler: setSoftwareUpdatesEnabled,
              },
            ].map(({ label, isActive, toggleHandler }, index) => (
              <div className="preference-item" key={index}>
                <span>{label}</span>
                <Toggle isActive={isActive} onToggle={toggleHandler} />
              </div>
            ))}
          </div>
        )}

        <hr />
        <div
          className={`notification-list ${showPreferences ? "collapsed" : "expanded"
            }`}
        >
          {loading ? (
            <div className="loading-indicator">
              <CircularProgress />
              <p>Loading notifications...</p>
            </div>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : notifications.length > 0 ? (
            notifications
              .filter((notif) => !notif.read)
              .map((notif) => (
                <div
                  className={`notification-item ${notif.read ? "read" : "unread"
                    }`}
                  key={notif.id}
                >
                  <div className="notification-content">
                    <p>{notif.message}</p>
                    {notif.linkUrl && (
                      <div
                        className="link"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(notif.linkUrl, { state: { user: notif.data.patientUser } });
                        }}
                      >
                        Go to the Patient Profile
                      </div>
                    )}
                    <small>{new Date(notif.createdAt).toLocaleString()}</small>
                  </div>
                  <button
                    className="dismiss-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDismiss(notif.id);
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))
          ) : (
            <p>No new notifications.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationPanel;
